import { useTranslation } from 'react-i18next';

import { Badge, Flex, Text, useColorModeValue } from '@bonusly/design-system';

import type { BizyScorecardMetric } from '../../../schemas/bizy-conversations';

interface BizyScorecardProps {
	/** Optional heading shown above the cards. */
	title?: string;
	/** 1–4 headline KPIs. */
	metrics: BizyScorecardMetric[];
	/** Drill into a metric — sends a verbatim message. Omitted in read-only views. */
	onSelectMetric?: (metric: BizyScorecardMetric) => void;
	/** Disable cards while a previous turn is still processing. */
	isDisabled?: boolean;
}

const SENTIMENT_COLOR: Record<BizyScorecardMetric['sentiment'], string> = {
	positive: 'green',
	neutral: 'gray',
	negative: 'red',
};

/**
 * Renders the Data Agent's Scorecard node: 1–4 KPI cards that lead a rich
 * answer as the at-a-glance verdict. Each card shows the metric label, its
 * formatted value, and an optional peer-percentile badge + median. When a drill
 * handler is provided the whole card is a tap target that sends a "tell me more"
 * message.
 *
 * The node also carries an optional `trend` series, intentionally NOT rendered
 * here: a context-free sparkline (no axis or period labels) reads as ambiguous.
 * Trend visualization belongs with the labeled mini-chart work in the charts
 * project; the schema keeps the field so that work needs no wire change.
 */
export function BizyScorecard({ title, metrics, onSelectMetric, isDisabled = false }: BizyScorecardProps) {
	const { t, i18n } = useTranslation('admin_bizy');
	// Brighter colorScheme border shade in dark mode so the subtle-fill badge
	// stays distinct from the card; softer in light mode.
	const badgeBorderShade = useColorModeValue('200', '400');

	// Locale-correct ordinal: Intl picks the plural category (en: 1st/2nd/3rd/…),
	// the suffix text comes from i18n per locale, and the "pct" word is localized.
	const percentileBadge = (pct: number) => {
		const n = Math.round(pct);
		const category = new Intl.PluralRules(i18n.language, { type: 'ordinal' }).select(n);
		const suffix = t(`scorecard.ordinalSuffix.${category}`, { defaultValue: t('scorecard.ordinalSuffix.other') });
		// Compose in TS rather than interpolate via t(): the ordinal is built here,
		// and t() only resolves the atomic localized "pct" word.
		const word = t('scorecard.percentileWord', { defaultValue: 'pct' });
		return `${n}${suffix} ${word}`;
	};

	const cards = metrics.filter((metric) => metric.label.trim().length > 0).slice(0, 4);
	if (cards.length === 0) return null;

	return (
		<Flex direction="column" gap={2} mb={4} data-testid="bizy-scorecard">
			{title ? (
				<Text fontSize="md" fontWeight="semibold" color="text.default">
					{title}
				</Text>
			) : null}
			<Flex gap={3} flexWrap="wrap">
				{cards.map((metric, idx) => {
					const color = SENTIMENT_COLOR[metric.sentiment];
					const tappable = !!onSelectMetric && !isDisabled;
					return (
						<Flex
							key={idx}
							onClick={tappable ? () => onSelectMetric?.(metric) : undefined}
							onKeyDown={
								tappable
									? (e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault();
												onSelectMetric?.(metric);
											}
										}
									: undefined
							}
							role={tappable ? 'button' : undefined}
							tabIndex={tappable ? 0 : undefined}
							aria-label={tappable ? t('drillInto', { label: metric.label, defaultValue: 'Drill into {{label}}' }) : undefined}
							direction="column"
							gap={1}
							flex="1 1 140px"
							minW="140px"
							textAlign="left"
							borderWidth="1px"
							borderColor="border.soft"
							borderRadius={12}
							px={3}
							py={2.5}
							opacity={isDisabled ? 0.5 : 1}
							cursor={tappable ? 'pointer' : 'default'}
							transition="background 0.15s ease, border-color 0.15s ease"
							{...(tappable && { _hover: { bg: 'secondary.bgAlpha', borderColor: 'border.default' } })}
						>
							<Text fontSize="xs" color="text.muted" lineHeight="short" noOfLines={1}>
								{metric.label}
							</Text>
							<Flex align="baseline" gap={2}>
								<Text fontSize="2xl" fontWeight="bold" color="text.default" lineHeight="short">
									{metric.value}
								</Text>
								{metric.percentile !== undefined ? (
									<Badge colorScheme={color} variant="subtle" fontSize="xs" borderWidth="1px" borderColor={`${color}.${badgeBorderShade}`}>
										{percentileBadge(metric.percentile)}
									</Badge>
								) : null}
							</Flex>
							{metric.peerMedian !== undefined ? (
								<Text fontSize="xs" color="text.muted" lineHeight="short">
									{t('scorecard.peerMedian', { value: metric.peerMedian, defaultValue: 'vs {{value}} peer median' })}
								</Text>
							) : null}
						</Flex>
					);
				})}
			</Flex>
		</Flex>
	);
}
